class ProductInformation extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
  }

  connectedCallback() {
    this.querySelector('pinfo-variant-picker')?.addEventListener(
      'input',
      this.handleVariantEvent.bind(this)
    );
    this.initQuantitySelector();
    this.initSwiper();
    this.initSellingPlan();
    this.syncMediaToUrlVariant();
    this.initAccordion();
    this.initAddonProducts();
    this.bindFormSubmit();
    this.bindBuyNow();
  }

  get productJSON() {
    if (!this._productJSON) {
      const el = this.querySelector('script[data-product-json]');
      this._productJSON = el ? JSON.parse(el.textContent) : null;
    }
    return this._productJSON;
  }

  get spAllocations() {
    if (!this._spAllocations) {
      const product = this.productJSON;
      if (product?.variants) {
        const variants = {};
        product.variants.forEach(v => {
          const allocations = {};
          (v.selling_plan_allocations || []).forEach(a => {
            allocations[String(a.selling_plan_id)] = {
              price: a.price,
              compare_at_price: a.compare_at_price
            };
          });
          variants[String(v.id)] = { price: v.price, allocations };
        });
        this._spAllocations = { variants };
      } else {
        const el = document.getElementById('sp-allocations-json');
        try {
          this._spAllocations = el ? JSON.parse(el.textContent) : { variants: {} };
        } catch (e) {
          this._spAllocations = { variants: {} };
        }
      }
    }
    return this._spAllocations;
  }

  get mediaOrder() {
    if (!this._mediaOrder) {
      this._mediaOrder = Array.from(
        this.querySelectorAll('.main-swiper .swiper-slide')
      ).map(s => s.dataset.media);
    }
    return this._mediaOrder;
  }

  handleVariantEvent(e) {
    const { variantUrl } = e.target.dataset;
    this.handleOptions(variantUrl);
    const params = new URLSearchParams(variantUrl.split('?')[1]);
    const variantId = Number(params.get('variant'));
    if (variantId) this.slideToVariantMedia(variantId);
  }

  slideToVariantMedia(variantId) {
    const product = this.productJSON;
    if (!product) return;
    const variant = product.variants.find(v => v.id === variantId);
    if (!variant || !variant.featured_media) return;
    const mediaId = String(variant.featured_media.id);

    const idx = this.mediaOrder.indexOf(mediaId);
    if (idx > -1 && this.mainSwiper) this.mainSwiper.slideTo(idx, 300);

    this.updateDesktopGrid(mediaId);
  }

  updateDesktopGrid(mediaId) {
    const grid = this.querySelector('.pdp-desktop-grid');
    if (!grid) return;

    const items  = Array.from(grid.querySelectorAll('.pdp-grid-item'));
    const target = items.find(el => el.dataset.mediaId === mediaId);
    const hero   = items[0];

    if (!target || target === hero) return;

    hero.classList.remove('pdp-grid-hero');
    hero.classList.add('pdp-grid-thumb');
    target.classList.remove('pdp-grid-thumb');
    target.classList.add('pdp-grid-hero');

    grid.insertBefore(target, hero);
  }

  syncMediaToUrlVariant() {
    const params = new URLSearchParams(window.location.search);
    const variantId = Number(params.get('variant'));
    if (!variantId) return;

    const product = this.productJSON;
    if (!product) return;

    const variant = product.variants.find(v => v.id === variantId);
    if (!variant || !variant.featured_media) return;

    const mediaId = String(variant.featured_media.id);

    const idx = this.mediaOrder.indexOf(mediaId);
    if (idx > -1 && this.mainSwiper) {
      this.mainSwiper.slideTo(idx, 0); 
    }

    this.updateDesktopGrid(mediaId);
  }

  initQuantitySelector() {
    const wrapper = this.querySelector('[data-quantity-selector]');
    if (!wrapper) return;
    const input = wrapper.querySelector('.qty-input');
    const dec   = wrapper.querySelector('.decrease');
    const inc   = wrapper.querySelector('.increase');
    const update = () => this.dispatchEvent(new CustomEvent('quantity-change', {
      detail: { quantity: Number(input.value) }, bubbles: true
    }));
    dec?.addEventListener('click', () => {
      let v = parseInt(input.value) || 1;
      if (v > (parseInt(input.min) || 1)) { input.value = v - 1; update(); }
    });
    inc?.addEventListener('click', () => {
      input.value = (parseInt(input.value) || 1) + 1;
      update();
    });
    input?.addEventListener('input', () => {
      if (input.value < 1) input.value = 1;
      update();
    });
  }

  initSwiper() {
    const container = this.querySelector('.pdp-media-container');
    if (!container) return;
    const mainEl  = container.querySelector('.main-swiper');
    const thumbEl = container.querySelector('.thumb-swiper');
    if (!mainEl || !thumbEl) return;

    this.thumbSwiper = new Swiper(thumbEl, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      watchSlidesProgress: true,
      freeMode: true,
      direction: 'horizontal',
    });

    this.mainSwiper = new Swiper(mainEl, {
      thumbs: { swiper: this.thumbSwiper },
      loop: false,
      on: {
        slideChange: () => this.updateNavButtons(),
        init:        () => this.updateNavButtons(),
      }
    });

    const prevBtn = mainEl.querySelector('.swiper-nav-prev');
    const nextBtn = mainEl.querySelector('.swiper-nav-next');
    prevBtn?.addEventListener('click', () => this.mainSwiper.slidePrev(300));
    nextBtn?.addEventListener('click', () => this.mainSwiper.slideNext(300));
    this._prevBtn = prevBtn;
    this._nextBtn = nextBtn;
  }

  updateNavButtons() {
    if (!this.mainSwiper) return;
    this._prevBtn?.classList.toggle('swiper-button-disabled', this.mainSwiper.isBeginning);
    this._nextBtn?.classList.toggle('swiper-button-disabled', this.mainSwiper.isEnd);
  }

  initSellingPlan() {
    const widget = this.querySelector('[data-selling-plan-widget]');
    if (!widget) return;
    const hiddenInput = widget.querySelector('#pinfo-selling-plan-input');

    const getVariantId = () => {
      const el = this.querySelector('input[name="id"]');
      return el ? String(el.value) : null;
    };

    const syncDisplay = (spId, priceEl, compareEl, badgeEl, freqSelect) => {
      const variantId   = getVariantId();
      const variantData = variantId ? (this.spAllocations.variants[variantId] || null) : null;
      const alloc       = (variantData && spId) ? (variantData.allocations[String(spId)] || null) : null;

      const price      = alloc ? this.formatMoney(alloc.price) : '';
      const compareRaw = alloc ? alloc.compare_at_price : 0;
      const compare    = (alloc && compareRaw > alloc.price) ? this.formatMoney(compareRaw) : '';

      if (priceEl)   priceEl.textContent = price;
      if (compareEl) compareEl.innerHTML  = compare ? '<s>' + compare + '</s>' : '';

      if (badgeEl && freqSelect) {
        const opt   = freqSelect.options[freqSelect.selectedIndex];
        const badge = opt ? (opt.dataset.badge || '') : '';
        badgeEl.textContent = badge;
        badgeEl.classList.toggle('sp-badge--hidden', !badge);
      }
    };

    widget.querySelectorAll('.sp-widget').forEach(group => {
      const cards         = group.querySelectorAll('.sp-card');
      const subscribeCard = group.querySelector('.sp-card--subscribe');
      const freqSelect    = group.querySelector('[data-frequency-select]');
      const badgeEl       = group.querySelector('[data-badge-display]');
      const priceEl       = group.querySelector('[data-subscribe-price-display]');
      const compareEl     = group.querySelector('[data-subscribe-compare-display]');

      cards.forEach(card => {
        card.addEventListener('click', () => {
          cards.forEach(c => {
            c.classList.remove('active');
            c.querySelector('.sp-hidden-radio').checked = false;
          });
          card.classList.add('active');
          card.querySelector('.sp-hidden-radio').checked = true;

          if (card.dataset.spCard === 'subscribe') {
            const spId = freqSelect
              ? (freqSelect.options[freqSelect.selectedIndex]?.value || '')
              : (card.querySelector('.sp-hidden-radio')?.dataset.sellingPlanId || '');
            if (hiddenInput) hiddenInput.value = spId;
            syncDisplay(spId, priceEl, compareEl, badgeEl, freqSelect);
          } else {
            if (hiddenInput) hiddenInput.value = '';
          }
        });
      });

      if (freqSelect) {
        freqSelect.addEventListener('change', () => {
          const opt  = freqSelect.options[freqSelect.selectedIndex];
          const spId = opt ? opt.value : '';
          if (subscribeCard?.classList.contains('active') && hiddenInput) {
            hiddenInput.value = spId;
          }
          syncDisplay(spId, priceEl, compareEl, badgeEl, freqSelect);
        });
        freqSelect.addEventListener('click', e => e.stopPropagation());

        const initOpt = freqSelect.options[freqSelect.selectedIndex];
        if (initOpt) syncDisplay(initOpt.value, priceEl, compareEl, badgeEl, freqSelect);
      } else if (subscribeCard) {
        const spRadio = subscribeCard.querySelector('.sp-hidden-radio');
        const spId    = spRadio ? spRadio.dataset.sellingPlanId : '';
        if (spId) syncDisplay(spId, priceEl, compareEl, badgeEl, null);
      }
    });

    if (hiddenInput) hiddenInput.value = '';
  }

  updateSellingPlanPrices(variantId) {
    const variantData = this.spAllocations.variants[String(variantId)];

    const oneTimePriceEl = this.querySelector('[data-one-time-price]');
    if (oneTimePriceEl && variantData) {
      oneTimePriceEl.textContent = this.formatMoney(variantData.price);
    }

    const planMap = {};
    (this.productJSON?.selling_plan_groups || []).forEach(g =>
      (g.selling_plans || []).forEach(p => { planMap[p.id] = p; })
    );

    this.querySelectorAll('.sp-widget').forEach(groupEl => {
      const freqSelect    = groupEl.querySelector('[data-frequency-select]');
      const badgeEl       = groupEl.querySelector('[data-badge-display]');
      const priceEl       = groupEl.querySelector('[data-subscribe-price-display]');
      const compareEl     = groupEl.querySelector('[data-subscribe-compare-display]');
      const subscribeCard = groupEl.querySelector('.sp-card--subscribe');

      let spId = null;
      if (freqSelect) {
        spId = freqSelect.options[freqSelect.selectedIndex]?.value || null;
      } else if (subscribeCard) {
        spId = subscribeCard.querySelector('.sp-hidden-radio')?.dataset.sellingPlanId || null;
      }

      const alloc      = (variantData && spId) ? (variantData.allocations[String(spId)] || null) : null;
      const price      = alloc ? this.formatMoney(alloc.price) : '';
      const compareRaw = alloc ? alloc.compare_at_price : 0;
      const compare    = (alloc && compareRaw > alloc.price) ? this.formatMoney(compareRaw) : '';

      if (priceEl)   priceEl.textContent = price;
      if (compareEl) compareEl.innerHTML  = compare ? '<s>' + compare + '</s>' : '';

      if (badgeEl && spId && variantData) {
        const plan = planMap[Number(spId)];
        const adj  = plan?.price_adjustments?.[0];
        let badge  = freqSelect
          ? (freqSelect.options[freqSelect.selectedIndex]?.dataset.badge || '')
          : (subscribeCard?.querySelector('.sp-hidden-radio')?.dataset.badge || '');

        if (adj?.value_type === 'price') {
          const saving = variantData.price - adj.value;
          badge = saving > 0 ? 'Save ' + this.formatMoney(saving) : 'Special Price';
          if (freqSelect) freqSelect.options[freqSelect.selectedIndex].dataset.badge = badge;
        }

        badgeEl.textContent = badge;
        badgeEl.classList.toggle('sp-badge--hidden', !badge);
      }
    });
  }

  formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    if (window.Shopify?.formatMoney) {
      const fmt = window.Shopify.money_format || window.Shopify.moneyFormat || '${{amount}}';
      const raw = window.Shopify.formatMoney(cents, fmt);
      return raw.replace(/[.,]00(?!\d)/, '');
    }
    const amount   = cents / 100;
    const hasCents = (amount % 1) !== 0;
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency,
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);
  }

  initAccordion() {
    const accordion = this.querySelector('.pinfo-accordion');
    if (!accordion) return;
    accordion.querySelectorAll('.accordion-trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const isOpen  = trigger.getAttribute('aria-expanded') === 'true';
        const content = trigger.nextElementSibling;
        accordion.querySelectorAll('.accordion-trigger').forEach(t => {
          t.setAttribute('aria-expanded', 'false');
          t.nextElementSibling?.classList.remove('open');
        });
        if (!isOpen) {
          trigger.setAttribute('aria-expanded', 'true');
          content?.classList.add('open');
        }
      });
    });
  }

  // ─── Addon Products ───────────────────────────────────────────────
  initAddonProducts() {
    this.querySelectorAll('[data-addon-card]').forEach(card => {
      const jsonEl = card.querySelector('script[data-addon-product-json]');
      if (!jsonEl) return;

      let addonProduct;
      try { addonProduct = JSON.parse(jsonEl.textContent); }
      catch (e) { console.warn('Addon product JSON parse error', e); return; }

      const addBtn  = card.querySelector('[data-addon-add-btn]');
      const priceEl = card.querySelector('[data-addon-price]');
      const selects = card.querySelectorAll('[data-addon-option-position]');

      const getSelectedVariant = () => {
        if (selects.length === 0) {
          return addonProduct.variants.find(v => v.available) || addonProduct.variants[0];
        }
        const selected = {};
        selects.forEach(sel => {
          selected[Number(sel.dataset.addonOptionPosition)] = sel.value;
        });
        return addonProduct.variants.find(v =>
          (!selected[1] || v.option1 === selected[1]) &&
          (!selected[2] || v.option2 === selected[2]) &&
          (!selected[3] || v.option3 === selected[3])
        );
      };

      const syncAddonState = () => {
        const variant = getSelectedVariant();
        if (!variant) return;
        if (addBtn) {
          addBtn.dataset.variantId = variant.id;
          addBtn.disabled = !variant.available;
          addBtn.textContent = variant.available
            ? (addBtn.dataset.defaultText || 'Add Now')
            : 'Out of Stock';
        }
        if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
      };

      if (addBtn) addBtn.dataset.defaultText = addBtn.textContent.trim();
      selects.forEach(sel => sel.addEventListener('change', () => syncAddonState()));
      syncAddonState();

      addBtn?.addEventListener('click', async () => {
        if (addBtn.disabled) return;
        const variantId = addBtn.dataset.variantId;
        if (!variantId) return;

        const originalText = addBtn.dataset.defaultText || 'Add Now';
        addBtn.classList.add('adding');
        addBtn.textContent = 'Adding...';
        addBtn.disabled = true;

        try {
          const resp = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: Number(variantId), quantity: 1 })
          });
          if (!resp.ok) throw new Error('Cart add failed');
          const data = await resp.json();
          this._openCart(data);
        } catch (err) {
          console.error('Addon add to cart error:', err);
          addBtn.textContent = 'Error — Try Again';
          addBtn.disabled = false;
        } finally {
          addBtn.classList.remove('adding');
          setTimeout(() => {
            const variant = getSelectedVariant();
            if (variant?.available) {
              addBtn.textContent = originalText;
              addBtn.disabled = false;
            }
          }, 1800);
        }
      });
    });
  }

  _openCart(cartData) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (typeof cartDrawer.renderContents === 'function') {
        fetch(window.Shopify.routes.root + 'cart.js')
          .then(r => r.json())
          .then(cart => cartDrawer.renderContents({ ...cart, sections: cartData.sections || {} }))
          .catch(() => cartDrawer.open?.());
      } else {
        cartDrawer.open?.();
      }
      return;
    }
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification) {
      if (typeof cartNotification.renderContents === 'function') {
        cartNotification.renderContents(cartData);
      } else {
        cartNotification.open?.();
      }
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { cartData }, bubbles: true }));
  }

  bindFormSubmit() {
    const form = this.querySelector('form[action$="/cart/add"]');
    if (!form) return;
    form.removeEventListener('submit', this._boundHandleAddToCart);
    this._boundHandleAddToCart = this.handleAddToCart.bind(this);
    form.addEventListener('submit', this._boundHandleAddToCart);
  }

  bindBuyNow() {
    const btn = this.querySelector('.p-info-btn-buy-now');
    if (!btn) return;
    btn.removeEventListener('click', this._boundHandleBuyNow);
    this._boundHandleBuyNow = this.handleBuyNow.bind(this);
    btn.addEventListener('click', this._boundHandleBuyNow);
  }

  handleBuyNow = async (e) => {
    e.preventDefault();
    const btn  = e.currentTarget;
    const form = this.querySelector('form[action$="/cart/add"]');
    if (!form) return;
    btn.disabled = true;
    btn.textContent = 'Processing...';
    const data = new FormData(form);
    const qty  = this.querySelector('.qty-input');
    if (qty) data.set('quantity', qty.value);
    this.appendSellingPlan(data);
    try {
      await fetch('/cart/add.js', { method: 'POST', body: data });
      window.location.href = '/checkout';
    } catch (err) {
      console.error('Buy Now failed:', err);
      btn.textContent = 'Try Again';
      btn.disabled = false;
    }
  }

  appendSellingPlan(formData) {
    const hiddenInput = this.querySelector('#pinfo-selling-plan-input');
    if (hiddenInput?.value) {
      formData.set('selling_plan', hiddenInput.value);
    } else {
      formData.delete('selling_plan');
    }
  }

  handleOptions(url) {
    this.fetchData(url);
    this.updateURL(url);
  }

  async fetchData(url) {
    const sectionUrl = `${url}&section_id=${this.sectionId}`;
    const resp = await fetch(sectionUrl);
    const txt  = await resp.text();
    const html = new DOMParser().parseFromString(txt, 'text/html');
    this.renderEl(html);
  }

  renderEl(html) {
    this.querySelectorAll('pinfo-variant-picker').forEach((el, i) => {
      const newEl = html.querySelectorAll('pinfo-variant-picker')[i];
      if (newEl) el.innerHTML = newEl.innerHTML;
    });

    this.querySelectorAll('[data-option-position]').forEach(input => {
      if (!input.checked) return;
      const pos   = input.dataset.optionPosition;
      const value = input.dataset.optionValue;
      const span  = this.querySelector(`[data-option-selected="${pos}"]`);
      if (span) span.textContent = value;
    });

    this.querySelectorAll('.pinfo-price-wrapper').forEach((el, i) => {
      const newEl = html.querySelectorAll('.pinfo-price-wrapper')[i];
      if (newEl) el.innerHTML = newEl.innerHTML;
    });
    this.querySelectorAll('.pinfo-form-wrapper').forEach((el, i) => {
      const newEl = html.querySelectorAll('.pinfo-form-wrapper')[i];
      if (newEl) {
        el.innerHTML = newEl.innerHTML;
        this.bindFormSubmit();
        this.bindBuyNow();
      }
    });
    const idInput = this.querySelector('input[name="id"]');
    if (idInput?.value) {
      const vid = Number(idInput.value);
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variantId: vid } }));
      this.slideToVariantMedia(vid);
      this.updateSellingPlanPrices(vid);
    }
  }

  updateURL(url) {
    if (typeof url !== 'string' || !url.startsWith('/')) return;
    if (this.dataset?.updateUrl === 'true') {
      window.history.pushState({}, '', url);
    }
  }

  handleAddToCart = (e) => {
    e.preventDefault();
    const btn          = this.querySelector('button[type="submit"]');
    const originalText = btn?.textContent || 'Add to Cart';
    if (btn) { btn.textContent = 'Adding...'; btn.disabled = true; btn.style.opacity = '0.75'; }

    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const form = this.querySelector('form[action$="/cart/add"]');
    const data = new FormData(form);
    const qty  = this.querySelector('.qty-input');
    if (qty) data.set('quantity', qty.value);
    this.appendSellingPlan(data);
    if (this.cart) data.append('sections', this.cart.getSectionsToRender().map(s => s.id));

    fetch(window.Shopify.routes.root + 'cart/add.js', { method: 'POST', body: data })
      .then(r => r.json())
      .then(res => { if (this.cart) this.cart.renderContents(res); })
      .finally(() => {
        if (btn) { btn.textContent = originalText; btn.disabled = false; btn.style.opacity = '1'; }
        if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        this.closest('.pqv-wrapper')?.querySelector('.pqv-close-btn')?.click();
      });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  customElements.define('product-information', ProductInformation);
});
